import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { uploadLogo } from "../../../api/directory";
import { fileUrl } from "../../../api/fileUrl";

export const LogoUpload = ({ value, onChange, label }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const shown = preview || fileUrl(value);

  const pick = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error(t("branches.logoTooLarge"));
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    try {
      const res = await uploadLogo(file);
      onChange(res.url);
      toast.success(t("branches.logoUploaded"));
    } catch {
      toast.error(t("errors.generic"));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-5 rounded-3xl bg-background p-4 shadow-neu-sm">
      <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-neu-in">
        {shown ? (
          <img src={shown} alt="logo" className="block h-full w-full object-cover" data-testid="branch-logo-preview" />
        ) : (
          <ImagePlus className="h-7 w-7 text-slate-400" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="label-xs">{label || t("branches.logo")}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t("branches.logoHint")}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="cursor-pointer">
            <input type="file" accept="image/*" className="hidden" data-testid="branch-logo-input" onChange={pick} />
            <span
              role="button"
              tabIndex={0}
              data-testid="branch-logo-btn"
              className="flex items-center gap-2 rounded-2xl bg-background px-5 py-2.5 text-sm font-semibold shadow-neu-sm transition-[box-shadow,color] duration-300 hover:text-primary active:shadow-neu-in"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              {t(shown ? "branches.replaceLogo" : "branches.uploadLogo")}
            </span>
          </label>
          {shown ? (
            <button
              type="button"
              data-testid="branch-logo-remove"
              onClick={() => {
                setPreview(null);
                onChange("");
              }}
              className="flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold text-destructive shadow-neu-sm transition-[box-shadow] duration-300 active:shadow-neu-in"
            >
              <Trash2 className="h-4 w-4" /> {t("branches.removeLogo")}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
